/** Stage E — Portfolio Equity Sparkline card
 *
 * Renders a real per-day equity series for one portfolio using recharts.
 * Never estimates missing history: when insufficient snapshots exist we show
 * a "History accumulating" state instead of a fake curve.
 */
import { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, Info } from "lucide-react";
import api from "@/lib/api";

const RANGES = ["1D", "1W", "1M", "3M", "YTD", "ALL"];

function fmt(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

export default function PortfolioSparkline({ portfolioId, portfolioKey, defaultRange = "1M", height = 60, showStats = true, colorClass = "" }) {
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    api.get(`/portfolios/sparkline/${portfolioId}`, { params: { range } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [portfolioId, range]);

  const series = data?.series || [];
  const stats = data?.stats || {};
  const insufficient = data?.insufficient_history;
  const up = (stats.return_abs ?? 0) >= 0;

  const chartData = useMemo(() => series.map(s => ({
    date: s.date, total_value: s.total_value,
  })), [series]);

  const color = up ? "var(--signal-green, #10b981)" : "var(--signal-red, #ef4444)";
  const baseline = stats.first;

  return (
    <div className="w-full" data-testid={`portfolio-sparkline-${portfolioKey || portfolioId}`}>
      {showStats && (
        <div className="flex items-center gap-3 mb-1 text-[10px]">
          {insufficient ? (
            <span className="label-cap text-muted-foreground inline-flex items-center gap-1">
              <Info size={10}/> History accumulating
            </span>
          ) : (
            <>
              {up ? <TrendingUp size={11} className="pnl-up"/> : <TrendingDown size={11} className="pnl-down"/>}
              <span className={`mono text-xs ${up ? "pnl-up" : "pnl-down"}`}>
                {up ? "+" : ""}{fmt(stats.return_abs)} ({up ? "+" : ""}{fmt(stats.return_pct)}%)
              </span>
              <span className="label-cap text-muted-foreground">{stats.range}</span>
            </>
          )}
          <div className="ml-auto flex items-center gap-0.5">
            {RANGES.map(r => (
              <button
                key={r}
                data-testid={`sparkline-range-${r}-${portfolioKey || "pf"}`}
                onClick={() => setRange(r)}
                className={`mono text-[9px] px-1.5 py-0.5 uppercase tracking-wider transition-colors ${range === r ? "text-foreground border-b border-current" : "text-muted-foreground hover:text-foreground"}`}
              >{r}</button>
            ))}
          </div>
        </div>
      )}
      <div style={{ height }} className={`w-full ${colorClass}`}>
        {loading ? (
          <div className="w-full h-full animate-pulse bg-muted/20"/>
        ) : chartData.length < 2 ? (
          <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground border border-dashed border-border/40">
            {chartData.length === 0 ? "No snapshots yet" : "First snapshot recorded — check back tomorrow"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={40}>
            <LineChart data={chartData} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
              <XAxis dataKey="date" hide/>
              <YAxis hide domain={["dataMin - 100", "dataMax + 100"]}/>
              <Tooltip
                cursor={{ stroke: "var(--border)", strokeWidth: 1 }}
                contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", fontSize: 11, padding: "4px 6px" }}
                labelFormatter={(v) => v}
                formatter={(v) => [`$${fmt(v)}`, "Equity"]}
              />
              {baseline !== undefined && <ReferenceLine y={baseline} strokeDasharray="2 2" stroke="var(--border)"/>}
              <Line type="monotone" dataKey="total_value" stroke={color} strokeWidth={1.5} dot={false} animationDuration={400}/>
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
